import { getFrontendOrderItemList, getSampleTypesByDeliveryMethod } from "./helpers/order";
import { getCustomerPromotionApi } from "@/api/va-order-page/pricing/item/promotion";
import { parseJson } from "@/utils";
import { OrderItem } from "@/utils/va-order-test/class/orderItem";
import { getStateAbbreviation } from "@/utils/va-order-test/parsePatient.js";
import moment from "moment";
const order = {
  namespaced: true,
  state: {
    paymentMethod: "",
    deliveryMethod: {
      blood: "",
      nonBlood: "shipToPatient", // default value
      dryIce: "shipToPatient"
    },
    labProcessingFee: 0,
    taxFee: 0,
    isUseTax: false,
    taxId: '',
    sendDate: {
      date: "",
      time: ""
    },
    sendNotification: {
      email: false,
      text: false
    },
    contactInfo: {
      email: sessionStorage.getItem("patient") 
        ? JSON.parse(sessionStorage.getItem("patient")).email : "",
      phone: sessionStorage.getItem("patient") 
      ? JSON.parse(sessionStorage.getItem("patient")).phone : ""
    },
    customerCredit: {
      amount: 0,
      isUse: false,
      availableCreditAmount: 0 
    },
    netPayInterval: {
      amount: 0,
      isUse: false
    },
    shippingFee: {
      importFee: 0,
      exportFee: 0,
      total: 0
    },
    conciergeFee: {
      amount: 0,
      isUse: false
    },
    consultationFee: {
      amount: 0,
      isUse: false,
      name: "Consultation Fee"
    },
    promoCode: "",
    promoDiscount: 0,
    customerSpecialPromos: [],
    plannedOrderList: [],
    plannedOrderListCountMax: 1,
    planAndSaveDiscountPercent: 30,
    sequenceOrderIdMap: {},
    editOrderSampleId: null,
    isPatientValidFollowUpOrder: {},
    inventoryUpdatedQuantity: {},
    patient: parseJson(sessionStorage.getItem("patient"))
  },
  getters: {
    patientAge(state) {
      let patientDob = moment(state.patient.birthdate, "MM/DD/YYYY");
      if (!patientDob.isValid()) {
        patientDob = "";
      }
      let age = patientDob ? moment().diff(patientDob, "years") : null;
      return age;
    },
    orderedSampleTypes(state, getters, rootState, rootGetters) {
      let selectedItems = rootGetters["orderTest/selectedItemList"];
      let editOrderItemList = rootState.orderTest.orderDraft.editOrderItemList;
      let orderdItems = editOrderItemList.concat(selectedItems);
      return {
        shipToPatient: getSampleTypesByDeliveryMethod(state.deliveryMethod, orderdItems, "shipToPatient"),
        suppliedByClient: getSampleTypesByDeliveryMethod(state.deliveryMethod, orderdItems, "suppliedByClient")
      }
    },
    // get utcTime format for selected date and time
    // date: MMMM,d YYYY
    // time: h:mm AM/PM
    sendDateInUtc(state) {
      let { date, time } = state.sendDate;
      if (!date) {
        return "";
      }
      let dateObj = new Date(date + " " + time);
      return moment(dateObj).utc().format();
    },

    isMarkupEligible(state, getters, rootState) {
      let userInfo = rootState.orderTest.user.userInfo;
      if (!userInfo) {
        return false;
      }
      const rawState = userInfo.customer_state 
        ? userInfo.customer_state 
        : userInfo.state 
          ?  userInfo.state 
          : '';
      const userState = rawState ? getStateAbbreviation(rawState) : '';
      const restrictedStates = ["NY", "NJ", "RI", "CA"];
      if (!restrictedStates.includes(userState.toUpperCase())) {
        return ["patientPayNow", "patientPayLater"].includes(state.paymentMethod);
      }
      return false;
    },

    orderTestsPrice(state, getters, rootState, rootGetters) {
      let editOrderItemList = rootState.orderTest.orderDraft.editOrderItemList;
      let price = 0;
      let firstOrderPrice = rootGetters["orderTest/selectedItemsPrice"];
      if (state.editOrderSampleId) {
        price = editOrderItemList.reduce((acc, item) => acc + item.total, 0);
        return Number((price + firstOrderPrice).toFixed(2));
      }
      let plannedOrdersPrice = state.plannedOrderList
        .reduce((acc, order) => acc + order.discountPrice, 0);
      let familyDiscountOrdersPrice = 0;
      if (rootGetters["orderTest/familyDiscount/familyDiscountEligible"]) {
        familyDiscountOrdersPrice = rootState.orderTest.familyDiscount.familyDiscountOrders
          .reduce((acc, order) => acc + order.price, 0);
      }
      return Number((firstOrderPrice + plannedOrdersPrice + familyDiscountOrdersPrice).toFixed(2));
    },
    totalLabProcessingFee(state) {
      let plannedOrdersLabProcessingFee = state.plannedOrderList
        .reduce((acc, order) => acc + order.labProcessingFee, 0);
      return Number((state.labProcessingFee + plannedOrdersLabProcessingFee).toFixed(2));
    },
    totalShippingFee(state) {
      let plannedOrdersShippingFee = state.plannedOrderList
        .reduce((acc, order) => acc + order.shippingFee.total, 0);
      return Number((state.shippingFee.total + plannedOrdersShippingFee).toFixed(2));
    },
    totalTaxFee(state) {
      let plannedOrdersTaxFee = state.plannedOrderList
        .reduce((acc, order) => acc + order.taxFee, 0);
      return Number((state.taxFee + plannedOrdersTaxFee).toFixed(2));
    },
    totalMarkupPrice(state, getters) {
      if (!getters.isMarkupEligible) {
        return 0;
      }
      let plannedOrdersMarkupPrice = state.plannedOrderList
        .reduce((acc, order) => acc + (order.markupPrice ? order.markupPrice : 0), 0);
      return Number((getters.firstOrderPriceDetails.markupPrice + plannedOrdersMarkupPrice).toFixed(2));
    },
    // price that coupon can be applied on (include plan&save)
    discountApplicablePrice(state, getters) {
      return getters.orderTestsPrice;
    },
    // total order price & lab processing fee  & concierge fee before any coupon discount and credit
    totalPriceBeforeDiscount(state, getters, rootState, rootGetters) {
      // TODO: may need to handle concierge fee for plan&save orders in future
      let conciergeFee = state.conciergeFee.isUse ? state.conciergeFee.amount : 0;
      let consultationFee = state.consultationFee.isUse ? state.consultationFee.amount : 0;
      let merchTotalPrice = rootGetters["orderTest/merchandise/merchTotalPrice"];
      let price = getters.discountApplicablePrice + getters.totalShippingFee + state.taxFee 
        + getters.totalMarkupPrice + getters.totalLabProcessingFee 
        + conciergeFee + consultationFee + merchTotalPrice;
      return Number(price.toFixed(2));
    },
    // total discount used on discountApplicablePrice
    discount(state, getters, rootState) {
      let discount = state.promoDiscount;
      if (state.editOrderSampleId) {
        let modifyOrder = rootState.orderTest.orderDraft.orderDraft;
        discount += modifyOrder.invoiceDiscount;
      }
      return getters.discountApplicablePrice > discount 
      ? discount : getters.discountApplicablePrice;
    },
    // order price after coupon discount, before client promo, and credit
    totalPriceAfterDiscount(state, getters) {
      return Number((getters.totalPriceBeforeDiscount - getters.discount).toFixed(2));
    },
    // order price after coupon discount and client promo, but before credit
    totalPriceAfterClientPromo(state, getters) {
      let price = getters.totalPriceAfterDiscount;
      // apply customer's special promo
      if (state.customerSpecialPromos.length > 0) {
        state.customerSpecialPromos.forEach(promo => {
          // require amount is reached, apply the promo to final price
          if (price >= promo.requireamount) {
            price = promo.isPercent === 1 ? price * promo.amount : price - promo.amount;
          }
        });
      }
      price = Number(price.toFixed(2));
      return price < 0 ? 0 : price;
    },
    // order price after client paid if any
    orderPriceAfterClientPaid(state, getters, rootState) {
      let price = getters.totalPriceAfterClientPromo;
      if (state.editOrderSampleId) {
        let modifyOrder = rootState.orderTest.orderDraft.orderDraft;
        let clientPaid = modifyOrder.clientPaid;
        price -= clientPaid;
      }
      return Number(price.toFixed(2));
    },
    // order price after coupon discount and credit
    // and after client paid (for edit order)
    finalOrderPrice(state, getters, rootState) {
      let finalPrice = getters.orderPriceAfterClientPaid;
      if (finalPrice < 0) {
        return finalPrice;  // not deduct customer credit if final price is less than 0 (case: edit order need refund)
      }
      if (state.customerCredit.isUse && state.customerCredit.amount > 0 && !state.netPayInterval.isUse) {
        finalPrice = finalPrice < state.customerCredit.amount ? 0 : finalPrice - state.customerCredit.amount;
      }
      return Number(finalPrice.toFixed(2));
    },
    // first order price details
    firstOrderPriceDetails(state, getters, rootState, rootGetters) {
      let selectedItems = rootGetters["orderTest/selectedItemList"];
      let editOrderItemList = rootState.orderTest.orderDraft.editOrderItemList;
      let markupPrice = getters.isMarkupEligible
        ? selectedItems.concat(editOrderItemList).reduce((acc, item) => acc + (item.markupPrice ? item.markupPrice : 0), 0)
        : 0;
      let orderTestsPrice = state.editOrderSampleId ? getters.orderTestsPrice : rootGetters["orderTest/selectedItemsPrice"];
      let discountApplicablePrice = orderTestsPrice;
      let conciergeFee = state.conciergeFee.isUse ? state.conciergeFee.amount : 0;
      let consultationFee = state.consultationFee.isUse ? state.consultationFee.amount : 0;
      let consultationFeeName = state.consultationFee.isUse ? state.consultationFee.name : "";
      let merchTotalPrice = rootGetters["orderTest/merchandise/merchTotalPrice"];

      let priceBeforeDiscount = discountApplicablePrice + state.shippingFee.total + state.taxFee + markupPrice + state.labProcessingFee + conciergeFee + consultationFee + merchTotalPrice;
      priceBeforeDiscount = Number(priceBeforeDiscount.toFixed(2));
      let discount = discountApplicablePrice > state.promoDiscount ?
        state.promoDiscount : discountApplicablePrice;
      let priceAfterDiscount = Number((priceBeforeDiscount - discount).toFixed(2));
      let priceAfterClientPromo = priceAfterDiscount;
      state.customerSpecialPromos.forEach(promo => {
        if (priceAfterClientPromo >= promo.requireamount) {
          priceAfterClientPromo = promo.isPercent === 1 ? priceAfterClientPromo * promo.amount : priceAfterClientPromo - promo.amount;
        }
      });
      priceAfterClientPromo = Number(priceAfterClientPromo.toFixed(2));
      let priceAfterClientPaid = priceAfterClientPromo;
      if (state.editOrderSampleId) {
        let modifyOrder = rootState.orderTest.orderDraft.orderDraft;
        let clientPaid = modifyOrder.clientPaid;
        priceAfterClientPaid -=  clientPaid;
      }
      let finalPrice = priceAfterClientPaid;
      if (state.customerCredit.isUse && state.customerCredit.amount > 0 && !state.netPayInterval.isUse) {
        finalPrice -= state.customerCredit.amount;
      }
      finalPrice = Number(finalPrice.toFixed(2));
      if (finalPrice < 0) {
        finalPrice = 0;
      }
      return {
        orderTestsPrice,
        labProcessingFee: state.labProcessingFee,
        shippingFee: state.shippingFee.total,
        conciergeFee,
        taxFee: state.taxFee,
        priceBeforeDiscount,
        markupPrice,
        consultationFee,
        consultationFeeName,
        merchTotalPrice,
        discount,
        priceAfterDiscount,
        priceAfterClientPromo,
        finalPrice,
      }
    },
    usedCredit(state, getters) {
      if (state.netPayInterval.isUse || !state.customerCredit.isUse) {
        return 0;
      }
      return getters.orderPriceAfterClientPaid > state.customerCredit.amount 
        ? state.customerCredit.amount : getters.orderPriceAfterClientPaid
    },
    priceDetails(state, getters, rootState, rootGetters) {
      return {
        orderTestsPrice: getters.orderTestsPrice,
        labProcessingFee: getters.totalLabProcessingFee,
        shippingFee: getters.totalShippingFee,
        priceBeforeDiscount: getters.totalPriceBeforeDiscount,
        discount: getters.discount,
        priceAfterDiscount: getters.totalPriceAfterDiscount,
        priceAfterClientPromo: getters.totalPriceAfterClientPromo,
        clientPromo: Number((getters.totalPriceAfterDiscount - getters.totalPriceAfterClientPromo).toFixed(2)),
        finalPrice: getters.finalOrderPrice,
        usedCredit: getters.usedCredit,
        conciergeFee: state.conciergeFee.isUse ? state.conciergeFee.amount : 0,
        taxFee: getters.totalTaxFee,
        markupPrice: getters.totalMarkupPrice,
        consultationFee: state.consultationFee.isUse ? state.consultationFee.amount : 0,
        consultationFeeName: state.consultationFee.isUse ? state.consultationFee.name : "",
        merchTotalPrice: rootGetters["orderTest/merchandise/merchTotalPrice"],
        // plan & save
        plannedTotalOriginalPrice: getters.plannedTotalOriginalPrice,
        plannedTotalDiscountPrice: getters.plannedTotalDiscountPrice,        
      }
    },

    // plan & save section 
    // planned original pirce, exlude plan deal, but include bundle deal
    plannedTotalOriginalPrice(state) {
      let price = 0;
      price =  state.plannedOrderList
        .reduce((acc, order) => acc + order.originalPrice, 0);
      return Number(price.toFixed(2));
    },
    // price discount price, indlue plan deal and bundle deal
    plannedTotalDiscountPrice(state) {
      let price = 0;
      price =  state.plannedOrderList
        .reduce((acc, order) => acc + order.discountPrice, 0);
      return Number(price.toFixed(2));
    },
    // plan & save section end
    orderItemList(state, getters, rootState, rootGetters) {
      let { editOrderItemList, selectedItemList } = rootGetters["orderTest/orderTestSupportObj"];
      return editOrderItemList.concat(selectedItemList).map(item => new OrderItem(item));
    },
  },
  mutations: {
    SET_PAYMENT_METHOD(state, paymentMethod) {
      state.paymentMethod = paymentMethod;
    },
    SET_DELIVERY_METHOD(state, { kit, deliveryMethod }) {
      state.deliveryMethod[kit] = deliveryMethod;
    },
    SET_LAB_PROCESSING_FEE(state, labProcessingFee) {
      state.labProcessingFee = labProcessingFee;
    },
    SET_TAX_FEE(state, { taxFee, isUseTax, taxId }) {
      state.taxFee = taxFee;
      state.isUseTax = isUseTax;
      state.taxId = taxId
    },
    SET_SEND_DATE(state, sendDate) {
      state.sendDate = sendDate;
    },
    SET_SEND_NOTIFICATION(state, { method, isSend }) {
      state.sendNotification[method] = isSend;
    },
    SET_CONTACT_INFO(state, { method, info }) {
      state.contactInfo[method] = info;
    },
    SET_CUSTOMER_CREDIT(state, { prop, value }) {
      state.customerCredit[prop] = value;
    },
    SET_NET_PAY_INTERVAL(state, { prop, value }) {
      state.netPayInterval[prop] = value;
    },
    SET_SHIPPING_FEE(state, shippingFeeObj) {
      state.shippingFee = shippingFeeObj;
    },
    RESET_SHIPPING_FEE(state) {
      state.shippingFee = {
        importFee: 0,
        exportFee: 0,
        total: 0
      };
    },
    SET_CONCIERGE_FEE(state, conciergeFee) {
      state.conciergeFee = conciergeFee;
    },
    SET_CONSULTATION_FEE(state, fee) {
      state.consultationFee = fee;
    },
    SET_CONSULTATION_FEE_USAGE(state, bool) {
      state.consultationFee.isUse = bool;
    },
    SET_CUSTOMER_SPECIAL_PROMOS(state, promos) {
      state.customerSpecialPromos = promos;
    },
    SET_PROMO_CODE(state, code) {
      state.promoCode = code;
    },
    SET_PROMO_DISCOUNT(state, discount) {
      state.promoDiscount = discount;
    },
    SET_PLANNED_ORDER_LIST(state, plannedOrderList) {
      state.plannedOrderList = plannedOrderList;
    },
    ADD_PLANNED_ORDER(state, plannedOrder) {
      state.plannedOrderList.push(plannedOrder);
    },
    REMOVE_PLANNED_ORDER(state, index) {
      state.plannedOrderList.splice(index, 1);
    },
    UPDATE_PLANNED_ORDER(state, { index, updatedPlannedOrder }) {
      state.plannedOrderList[index] = updatedPlannedOrder;
    },
    SET_SEQUENCE_ORDER_ID_MAP(state, sequenceMap) {
      state.sequenceOrderIdMap = sequenceMap;
    },
    SET_EDIT_ORDER_SAMPLE_ID(state, sampleId) {
      if (!sampleId) {
        state.editOrderSampleId = null;
      }
      state.editOrderSampleId = parseInt(sampleId);
    },
    SET_IS_PATIENT_VALID_FOLLOW_UP_ORDER(state, isPatientValidFollowUpOrder) {
      state.isPatientValidFollowUpOrder = isPatientValidFollowUpOrder;
    },
    SET_UPDATED_INVENTORY(state, inventoryUpdatedQuantity) {
      state.inventoryUpdatedQuantity = inventoryUpdatedQuantity
    },
    SET_PATIENT(state, patient) {
      state.patient = patient;
    }
  },
  actions: {
    setPaymentMethod({ commit }, paymentMethod) {
      commit("SET_PAYMENT_METHOD", paymentMethod);
    },
    setDeliveryMethod({ commit }, { kit, deliveryMethod }) {
      commit("SET_DELIVERY_METHOD", { kit, deliveryMethod });
    },
    setLabProcessingFee({ commit }, labProcessingFee) {
      commit("SET_LAB_PROCESSING_FEE", labProcessingFee);
    },
    setTaxFee({ commit }, { taxFee, isUseTax, taxId }) {
      commit("SET_TAX_FEE", { taxFee, isUseTax, taxId });
    },
    setUpdateInventory({ commit }, inventoryUpdatedQuantity) {
      commit("SET_UPDATED_INVENTORY", inventoryUpdatedQuantity);
    },
    setSendDate({ commit }, sendDate) {
      commit("SET_SEND_DATE", sendDate);
    },
    setSendNotification({ commit }, { method, isSend }) {
      commit("SET_SEND_NOTIFICATION", { method, isSend });
    },
    setContactInfo({ commit }, { method, info }) {
      commit("SET_CONTACT_INFO", { method, info });
    },
    setCustomerCredit({ commit }, { prop, value }) {
      commit("SET_CUSTOMER_CREDIT", { prop, value });
    },
    setNetPayInterval({ commit }, { prop, value }) {
      commit("SET_NET_PAY_INTERVAL", { prop, value });
    },
    setShippingFee({ commit }, shippingFeeObj) {
      commit("SET_SHIPPING_FEE", shippingFeeObj);
    },
    setConciergeFee({ commit }, conciergeFee) {
      commit("SET_CONCIERGE_FEE", conciergeFee);
    },
    setConsultationFee({ commit }, fee) {
      commit("SET_CONSULTATION_FEE", fee);
    },
    setConsultationFeeUsage({ commit }, bool) {
      commit("SET_CONSULTATION_FEE_USAGE", bool)
    },
    async setCustomerSpecialPromos({ commit, rootState }) {
      let customer_id = rootState.orderTest.user.userInfo.customer_id;
      if (!customer_id) {
        return;
      }
      let { data } = await getCustomerPromotionApi({ customer_id });
      // temporarily prevent errors from CustomerSpecialPromos
      if (data) {
        commit("SET_CUSTOMER_SPECIAL_PROMOS", data);
      }
      
    },
    setPromoCode({ commit }, code) {
      commit("SET_PROMO_CODE", code);
    },
    setPromoDiscount({ commit }, discount) {
      commit("SET_PROMO_DISCOUNT", discount);
    },
    setPlannedOrderList({ commit }, plannedOrderList) {
      commit("SET_PLANNED_ORDER_LIST", plannedOrderList);
    },
    addPlannedOrder({ commit }, plannedOrder) {
      commit("ADD_PLANNED_ORDER", plannedOrder);
    },
    removePlannedOrder({ commit }, index) {
      commit("REMOVE_PLANNED_ORDER", index);
    },
    updatePlannedOrder({ commit }, { index, updatedPlannedOrder }) {
      // update total price for plannedOrder
      let { discountPrice, labProcessingFee, shippingFee } = updatedPlannedOrder;
      updatedPlannedOrder.totalPrice = discountPrice + labProcessingFee + shippingFee.total;
      commit("UPDATE_PLANNED_ORDER", { index, updatedPlannedOrder });
    },
    setSequenceOrderIdMap({ commit }, sequenceMap) {
      commit("SET_SEQUENCE_ORDER_ID_MAP", sequenceMap);
    },
    setEditOrderSampleId({ commit }, sampleId) {
      commit("SET_EDIT_ORDER_SAMPLE_ID", sampleId);
    },
    setIsPatientValidFollowUpOrder({ commit }, isPatientValidFollowUpOrder) {
      commit("SET_IS_PATIENT_VALID_FOLLOW_UP_ORDER", isPatientValidFollowUpOrder);
    },
    setPatient({ commit }, patient) {
      commit("SET_PATIENT", patient);
    }
  }
};

export default order;